import { Dispatch, useCallback, useState } from 'react';
import instance from '../../api/api';
import { INotification, NotificationType } from '../../interfaces/notification';
import Notification, { notificationImages } from '../../components/notifications/notification.component';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from "react-redux";
import { setAuthentication } from "../../store/actionCreators";
import { setCookies } from "../../components/_helper/cookies";
import { useTranslation } from "react-i18next";
import config from '../../config/config';

interface ISignInWithGoogleButtonProps {
    context: "register" | "login";
    invitation?: string;
    referral?: string;
    familyVip?: string;
    onSuccess?: (userData: any) => void;
    onError?: (error: any) => void;
}

// Module-level state — persists across re-renders and mounts
let gisScriptLoading = false;
let gisInitialized = false;
let _hiddenBtn: HTMLElement | null = null;
// Stable callback ref so the once-initialized GIS instance always calls the latest closure
let _credentialHandler: ((r: { credential: string }) => void) | undefined;

const GoogleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
);

const SignInWithGoogleButton = (props: ISignInWithGoogleButtonProps) => {
    const { t } = useTranslation();
    const dispatch: Dispatch<any> = useDispatch();
    const setAuth = useCallback((isAuthenticated: boolean) => dispatch(setAuthentication(isAuthenticated)), [dispatch]);
    const navigate = useNavigate();

    const [notification, setNotification] = useState<INotification | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleGoogleCredential = async (credentialResponse: { credential: string }) => {
        if (!credentialResponse.credential) return;

        try {
            const response = await instance.post("/auth/login/google",
                props.invitation
                    ? { idToken: credentialResponse.credential, invitation: props.invitation }
                    : { idToken: credentialResponse.credential }
            );

            if (response?.data?.user?._id) {
                setCookies(response.data.token, response.data.refreshToken);
                localStorage.setItem('userid', response.data.user._id);
                setAuth(true);

                if (props.onSuccess) props.onSuccess(response.data.user);

                if (response.data.accountLinked) {
                    setNotification({
                        icon: notificationImages.happiness,
                        title: t("Account Linked"),
                        text: t("Your Google account has been linked to your existing account"),
                        success: NotificationType.success
                    });
                }

                if (response.data.newUser) {
                    setNotification({
                        icon: notificationImages.happiness,
                        title: t("Account Verified by Google"),
                        text: t("Your Simirity Account has been verified by Google"),
                        success: NotificationType.success
                    });
                }

                const modalTimeout = (response.data.accountLinked || response.data.newUser)
                    ? config.modalCloseTimeout
                    : 100;

                setTimeout(() => {
                    if (!response.data.newUser) {
                        navigate("/");
                    } else {
                        const user = response.data.user;
                        navigate("/registration-details", {
                            state: {
                                fromGoogle: true,
                                firstname: user.firstname || '',
                                lastname: user.lastname || '',
                                email: user.email,
                                avatar: user.avatar || '',
                                referral: props.referral || '',
                                familyVip: props.familyVip || ''
                            }
                        });
                    }
                }, modalTimeout);
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || t("Failed to sign in with Google. Please try again.");
            setNotification({
                icon: notificationImages.crying,
                title: t("Sign In Failed"),
                text: errorMessage,
                success: NotificationType.problem
            });
            if (props.onError) props.onError(err);
        }
    };

    const initAndClick = () => {
        if (!gisInitialized) {
            window.google!.accounts.id.initialize({
                client_id: window.__RUNTIME_CONFIG__.REACT_APP_GOOGLE_CLIENT_ID,
                callback: (r) => _credentialHandler?.(r),
            });

            const container = document.createElement('div');
            container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
            document.body.appendChild(container);
            window.google!.accounts.id.renderButton(container, { type: 'standard', size: 'large' });
            _hiddenBtn = container.querySelector<HTMLElement>('div[role="button"]');
            gisInitialized = true;
        }

        if (_hiddenBtn) {
            _hiddenBtn.click();
            setIsLoading(false);
        } else {
            setIsLoading(false);
            setNotification({
                icon: notificationImages.crying,
                title: t("Sign In Failed"),
                text: t("Failed to load Google sign-in. Please try again."),
                success: NotificationType.problem
            });
        }
    };

    const handleButtonClick = () => {
        if (isLoading) return;
        setIsLoading(true);

        // Always point the module-level ref to the current closure
        _credentialHandler = handleGoogleCredential;

        if (window.google?.accounts?.id) {
            initAndClick();
            return;
        }

        if (gisScriptLoading) return;
        gisScriptLoading = true;

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => { gisScriptLoading = false; initAndClick(); };
        script.onerror = () => {
            gisScriptLoading = false;
            setIsLoading(false);
            setNotification({
                icon: notificationImages.crying,
                title: t("Sign In Failed"),
                text: t("Failed to load Google sign-in. Please check your connection and try again."),
                success: NotificationType.problem
            });
        };
        document.head.appendChild(script);
    };

    const label = props.context === "register" ? t("Sign up with Google") : t("Sign in with Google");

    return (
        <div className="google-auth-section">
            <button
                className="google-custom-btn"
                onClick={handleButtonClick}
                disabled={isLoading}
                type="button"
            >
                <GoogleIcon />
                {isLoading ? t("Loading...") : label}
            </button>

            {notification && (
                <Notification
                    data={notification}
                    close={() => setNotification(null)}
                />
            )}
        </div>
    );
};

export default SignInWithGoogleButton;